import { useState } from "react";
import { Button, CircularProgress, Stack, TextField } from "@mui/material";
import { GovItemDetail, transitionItem } from "../../../../utils/icGovernance";
import { kindLabel } from "../governanceStatus";
import ActionError from "./ActionError";
import SideCard from "./SideCard";
import { ItemActionProps } from "./types";
import { approveButtonSx, neutralButtonSx } from "./ui";
import { useGovAction } from "./useGovAction";

type Move = "CLOSED" | "IN_PROGRESS" | "NEW";

const MOVES: Record<Move, { label: string; done: string }> = {
  CLOSED: { label: "Confirm fixed and close", done: "closed" },
  IN_PROGRESS: { label: "Not fixed, send back", done: "sent back" },
  NEW: { label: "Reopen", done: "reopened" },
};

const isMove = (status: string): status is Move => Object.prototype.hasOwnProperty.call(MOVES, status);

/** One button per `permissions.transitions` entry, with an optional comment. */
const ActionsCard = ({ item, onUpdated, onReload }: { item: GovItemDetail } & ItemActionProps) => {
  const [comment, setComment] = useState("");
  const [pending, setPending] = useState<Move | null>(null);
  const action = useGovAction();
  const moves = (item.permissions?.transitions ?? []).filter(isMove);
  if (moves.length === 0) return null;

  const move = async (status: Move) => {
    setPending(status);
    const detail = await action.run(() => transitionItem(item.id, status, comment.trim() || undefined));
    setPending(null);
    if (detail) {
      setComment("");
      onUpdated(detail, { text: `${kindLabel(item.kind)} ${MOVES[status].done}` });
    }
  };

  return (
    <SideCard title="Actions">
      <TextField
        label="Comment (optional)"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        multiline
        minRows={2}
        fullWidth
        size="small"
        disabled={action.busy}
      />
      <Stack spacing={1} sx={{ mt: 1.5 }}>
        {moves.map((status) => (
          <Button
            key={status}
            fullWidth
            variant={status === "CLOSED" ? "contained" : "outlined"}
            color={status === "CLOSED" ? "success" : "inherit"}
            disableElevation
            onClick={() => move(status)}
            disabled={action.busy}
            startIcon={pending === status ? <CircularProgress size={16} color="inherit" /> : null}
            sx={status === "CLOSED" ? approveButtonSx : neutralButtonSx}
          >
            {MOVES[status].label}
          </Button>
        ))}
      </Stack>
      <ActionError action={action} onReload={onReload} />
    </SideCard>
  );
};

export default ActionsCard;
